// ค้างชำระ — one patient's full debt ledger, as a right slide-in drawer opened
// from a row on /reports/outstanding.
//
// WHY A DRAWER. The list answers "who owes us"; this answers "what for, and is
// it worth chasing". Front desk needs both in one place before picking up the
// phone — a row that only links out to the patient chart made them leave the
// worklist to find out, and lose their position in it.
//
// SIGNS. Lines are shown EXACTLY as they are recorded — no sign flipping. A
// NEGATIVE `priceXunit` is the debt itself (-14,500 ค้างชำระ); a POSITIVE one is
// money the patient has paid back (60,000 ชำระคืน, emerald). Owner-confirmed
// 2026-08-13: do not normalise these to positives. Every other billing surface
// in the CRM shows the raw figure, and a drawer that quietly negated it would
// disagree with the patient ledger for the same line.
//
// The ยอดค้างชำระ headline is the one deliberate exception, and it is not a
// conversion of any single line: it is -(sum of every line), i.e. what is still
// owed, which reads naturally as a positive amount to collect.
//
// The total is taken from the endpoint, never re-summed here, and that endpoint
// shares its where-clause with the one behind the list — so this figure and the
// row that opened it cannot disagree. Two different numbers for the same patient
// at the counter is the failure mode worth engineering against.
//
// Deliberately carries NO phone or address, same as the list. "เปิดประวัติคนไข้"
// is the way to contact details — that link lives here now rather than on every
// row, so the table stays a scannable worklist.

import { useEffect, useMemo, useState } from 'react'
import { X, ExternalLink, Landmark, CalendarClock, Receipt } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { fmtBill as fmt } from '../../lib/billing.js'
import { fmtDate } from '../../lib/datetime.js'
import { nameInitial } from '../../lib/text.js'
import { STATUS_TONES, statusTone } from '../../data/patientStatuses.js'
import { Loading, Empty } from '../ui/DrawerPrimitives.jsx'

// "how cold is this case" — the number that actually decides whether to chase.
// A debt whose last treatment was 5 years ago is a different job from one from
// last week, and the raw date makes you do that arithmetic yourself.
function ageLabel(v) {
  if (!v) return ''
  const then = new Date(String(v).slice(0, 10))
  if (Number.isNaN(then.getTime())) return ''
  const days = Math.floor((Date.now() - then.getTime()) / 86400000)
  if (days < 0) return ''
  if (days < 31) return `${days} วันก่อน`
  const months = Math.floor(days / 30.44)
  if (months < 12) return `${months} เดือนก่อน`
  const years = Math.floor(days / 365.25)
  const rem = Math.floor((days - years * 365.25) / 30.44)
  return rem ? `${years} ปี ${rem} เดือนก่อน` : `${years} ปีก่อน`
}

export default function OutstandingDrawer({ row, branchName = {}, onClose }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const pid = Number(row?.patient_id) || 0

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    if (!pid) return
    const ctrl = new AbortController()
    setData(null); setErr('')
    apiGet('/api/billing-outstanding-patient', { patient_id: pid }, { signal: ctrl.signal })
      .then(setData)
      .catch((e) => { if (e?.name !== 'AbortError') setErr(e?.message || 'โหลดข้อมูลไม่สำเร็จ') })
    return () => ctrl.abort()
  }, [pid])

  // Fall back to the row while the ledger loads, so the header never flashes
  // empty — the list already knows the name, HN and amount.
  const p = data?.patient || {}
  const name = p.fullname || row?.fullname || '—'
  const hn = p.HN || row?.HN || ''
  const status = (p.status ?? row?.status ?? '').trim()
  const lastTx = p.last_treatment ?? row?.last_treatment ?? null
  const owed = data ? data.outstanding : row?.outstanding
  const tone = STATUS_TONES[statusTone(status)] || STATUS_TONES.none

  const lines = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data])

  if (!row) return null

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[480px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">

        <div className="px-5 py-3.5 bg-white border-b border-slate-200 flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-brand-50 text-brand-600 text-[15px] font-semibold flex items-center justify-center shrink-0">
            {nameInitial(name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900 leading-tight truncate">{name}</div>
            <div className="text-[12px] text-slate-400 mt-1 flex items-center gap-1.5 flex-wrap">
              {hn && <span className="font-mono">HN {hn}</span>}
              {status && (
                <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] ring-1 ${tone.pill}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />{status}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" title="ปิด"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-[12px] text-slate-400 mb-0.5">ยอดค้างชำระ</div>
            <div className="text-[26px] font-bold text-slate-900 tabular-nums leading-tight">{fmt(owed)} <span className="text-[15px] font-semibold text-slate-400">บาท</span></div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-x-8 gap-y-2 text-[12.5px]">
              <div className="flex items-center gap-1.5 text-slate-500">
                <Receipt size={13} className="text-slate-300" />
                {data ? `${data.lines} รายการ` : `${row?.lines ?? '—'} รายการ`}
              </div>
              <div className="flex items-center gap-1.5 text-slate-500">
                <CalendarClock size={13} className="text-slate-300" />
                รักษาล่าสุด {lastTx ? fmtDate(lastTx) : '—'}
                {ageLabel(lastTx) && <span className="text-slate-400">· {ageLabel(lastTx)}</span>}
              </div>
            </div>
          </div>

          {/* The row's old external link, relocated. One place to leave the
              worklist from, instead of a link on every row. */}
          <a
            href={`/patient/${pid}/treatments`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 hover:text-brand-700"
          >
            เปิดประวัติคนไข้ <ExternalLink size={13} className="text-slate-400" />
          </a>

          <section>
            <div className="flex items-center gap-2 mb-2">
              <Landmark size={15} className="text-slate-400" />
              <h3 className="text-[13px] font-semibold text-slate-700">รายการค้างชำระทั้งหมด</h3>
              {data && <span className="text-[11px] text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5">{lines.length}</span>}
            </div>

            {err && <p className="text-[12.5px] text-amber-700">โหลดไม่สำเร็จ: {err}</p>}
            {!err && data == null && <Loading />}
            {!err && data != null && !lines.length && <Empty>ไม่พบรายการค้างชำระ</Empty>}

            {!err && data != null && lines.length > 0 && (
              <ul className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
                {lines.map((b) => {
                  const x = Number(b.priceXunit) || 0
                  // Printed as recorded. NEGATIVE = the ค้างชำระ line itself,
                  // POSITIVE = a ชำระคืน. fmtBill renders the minus sign, so
                  // nothing here adds or strips one.
                  const isCharge = x < 0
                  const qty = Number(b.N) || 1
                  const bn = branchName[String(b.tx_branch_id ?? b.branch_id)] || ''
                  return (
                    <li key={b.id} className="px-3.5 py-2.5 flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-slate-800 leading-snug">
                          {b.detail?.trim() || b.type_name || 'ค้างชำระ'}
                        </div>
                        <div className="text-[11.5px] text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                          <span>{fmtDate(b.tx_date || b.date)}</span>
                          {qty > 1 && <span>· ×{qty}</span>}
                          {bn && <span>· {bn}</span>}
                          {b.billing_type_id === 153 && <span>· ปรับปรุง</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-[13.5px] font-semibold tabular-nums ${isCharge ? 'text-slate-900' : 'text-emerald-600'}`}>
                          {fmt(x)}
                        </div>
                        <div className={`text-[10.5px] ${isCharge ? 'text-rose-500' : 'text-emerald-500'}`}>
                          {isCharge ? 'ค้างชำระ' : 'ชำระคืน'}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}

            <p className="mt-2.5 text-[11.5px] text-slate-400 leading-relaxed">
              แสดงตามค่าที่บันทึกไว้จริง · ยอดติดลบคือรายการที่ค้างชำระ ·
              ยอดบวกคือเงินที่คนไข้ชำระคืนแล้ว · ผลรวมทุกรายการคือยอดค้างชำระด้านบน
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
